import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Check, Loader2, Box, Boxes, Layers, Sparkles, Zap, Gamepad2, Package, CheckCircle2, AlertTriangle } from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { apiFetch } from "../../utils/api";
import { OptionCard } from "../design/OptionCard";
import { Select } from "../design/Select";
import { Input } from "../design/Input";
import { Button } from "../design/Button";
import ToggleSwitch from "../common/ToggleSwitch";
import { cn } from "../../utils/cn";
import toast from "react-hot-toast";

type Game = "java" | "bedrock" | "modpack" | "steam";
type JavaType = "vanilla" | "fabric" | "paper" | "purpur";
interface VObj { id: string; url: string | null; release: boolean; }

const TYPES: { id: JavaType; title: string; subtitle: string; icon: React.ReactNode }[] = [
  { id: "vanilla", title: "Vanilla", subtitle: "Das pure Minecraft, ohne Erweiterungen", icon: <Box size={20} /> },
  { id: "fabric", title: "Fabric", subtitle: "Leichtgewichtige Mods", icon: <Layers size={20} /> },
  { id: "paper", title: "Paper", subtitle: "Plugins, hohe Performance", icon: <Sparkles size={20} /> },
  { id: "purpur", title: "Purpur", subtitle: "Paper-Fork mit mehr Optionen", icon: <Zap size={20} /> },
];

export const ServerSetupWizard = observer(({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  const [game, setGame] = useState<Game>("java");
  const [type, setType] = useState<JavaType>("paper");
  const [versionObjs, setVersionObjs] = useState<VObj[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [name, setName] = useState("");
  const [port, setPort] = useState(25565);
  const [ram, setRam] = useState(4);
  const [phase, setPhase] = useState<"steps" | "installing" | "done" | "error">("steps");
  const [installMsg, setInstallMsg] = useState("");
  const [stepIdx, setStepIdx] = useState(0);

  // Steam
  const [steamGames, setSteamGames] = useState<any[]>([]);
  const [steamSlug, setSteamSlug] = useState("");
  const [customAppId, setCustomAppId] = useState("");
  const [customPreview, setCustomPreview] = useState<{ name: string; image: string | null } | null>(null);
  const [steamCfg, setSteamCfg] = useState<any>({});

  // Java picks a loader + version; Bedrock/Modpack go straight to config; Steam
  // inserts a game-picker step (ARK, Valheim, …) before config.
  const flow = useMemo<string[]>(
    () =>
      game === "java" ? ["game", "type", "version", "config"]
      : game === "steam" ? ["game", "steamgame", "config"]
      : ["game", "config"],
    [game]
  );
  const step = flow[Math.min(stepIdx, flow.length - 1)];

  // Close on Escape while still on the input steps (not mid-install).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && phase === "steps") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [phase, onClose]);

  // Suggest a free port when the game changes (steam ports are set once a game is picked).
  // Java-Server: naechsten freien Port ab 25565 (breiter Bereich, damit sich Server
  // nie gegenseitig blockieren). Bedrock: naechster freier ab 19132.
  useEffect(() => {
    if (game === "steam") return;
    const used = new Set(serverStore.servers.map((s) => s.port));
    const firstFree = (start: number, end: number) => {
      for (let p = start; p <= end; p++) if (!used.has(p)) return p;
      return start;
    };
    setPort(game === "bedrock" ? firstFree(19132, 19170) : firstFree(25565, 25610));
  }, [game]);

  // Load the Steam catalog the first time Steam is chosen.
  useEffect(() => {
    if (game !== "steam" || steamGames.length) return;
    serverStore.fetchSteamCatalog().then((d: any) => setSteamGames(d.games || []));
  }, [game]);

  // Custom AppID → keyless Steam-Store preview (name + image), debounced.
  useEffect(() => {
    if (steamSlug !== "custom" || !/^\d{2,}$/.test(customAppId.trim())) { setCustomPreview(null); return; }
    const id = customAppId.trim();
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/proxies/steam/appdetails?appid=${id}`);
        const d = res.ok ? await res.json() : null;
        setCustomPreview(d?.success ? { name: d.name, image: d.image } : null);
      } catch { setCustomPreview(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [customAppId, steamSlug]);

  // When a Steam game is selected: seed its config, name and a free default port.
  useEffect(() => {
    if (game !== "steam" || !steamSlug) return;
    const g = steamGames.find((x) => x.slug === steamSlug);
    if (!g) return;
    setSteamCfg({ ...(g.defaults || {}) });
    if (g.defaults?.name) setName(g.defaults.name);
    const used = new Set(serverStore.servers.map((s) => s.port));
    let p = g.defaultPorts?.[0]?.port || 27015;
    while (used.has(p)) p++;
    setPort(p);
  }, [steamSlug, steamGames]);

  // Fetch versions when we need them (java, on type change)
  useEffect(() => {
    if (game !== "java") return;
    let cancelled = false;
    (async () => {
      setLoadingVersions(true);
      setSelectedVersion("");
      try {
        let objs: VObj[] = [];
        if (type === "paper" || type === "purpur") {
          const res = await apiFetch(`/api/proxies/${type}/versions`);
          const data = res.ok ? await res.json() : [];
          objs = (data as string[]).map((v) => ({ id: v, url: null, release: true }));
        } else {
          const res = await apiFetch("/api/proxies/vanilla/versions");
          const data = res.ok ? await res.json() : [];
          objs = (data as any[]).map((v) => ({ id: v.id, url: v.url, release: v.type === "release" }));
        }
        if (!cancelled) setVersionObjs(objs);
      } finally {
        if (!cancelled) setLoadingVersions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [type, game]);

  const filteredVersions = useMemo(() => (showSnapshots ? versionObjs : versionObjs.filter((v) => v.release)), [versionObjs, showSnapshots]);

  // Default to the newest release once versions load
  useEffect(() => {
    if (filteredVersions.length && !filteredVersions.some((v) => v.id === selectedVersion)) {
      setSelectedVersion(filteredVersions[0].id);
    }
  }, [filteredVersions]);

  const next = () => setStepIdx((i) => Math.min(flow.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const canNext =
    step === "game" ? true :
    step === "type" ? true :
    step === "version" ? Boolean(selectedVersion) && !loadingVersions :
    step === "steamgame" ? Boolean(steamSlug) && (steamSlug !== "custom" || /^\d+$/.test(customAppId.trim())) :
    true;

  const doInstall = async (id: string) => {
    if (game === "bedrock") {
      return apiFetch(`/api/servers/${id}/bedrock/install`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ restart: false }) });
    }
    if (type === "fabric") {
      return apiFetch(`/api/servers/${id}/fabric`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mcVersion: selectedVersion, loaderVersion: "0.16.10" }) });
    }
    if (type === "paper" || type === "purpur") {
      return apiFetch(`/api/servers/${id}/paper/install`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mcVersion: selectedVersion, flavor: type, restart: false }) });
    }
    const vObj = versionObjs.find((v) => v.id === selectedVersion);
    return apiFetch(`/api/servers/${id}/vanilla/install`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ versionUrl: vObj?.url, createBackup: false, restart: false }) });
  };

  const steamGame = steamGames.find((g) => g.slug === steamSlug);

  const create = async () => {
    if (!name.trim()) return;
    // Valheim-Regeln früh prüfen (sonst startet der Server später nicht).
    if (game === "steam" && steamSlug === "valheim") {
      const pw = String(steamCfg.password || "");
      if (pw.length < 5) { toast.error("Valheim braucht ein Join-Passwort mit mindestens 5 Zeichen."); return; }
      if (name.trim().toLowerCase().includes(pw.toLowerCase())) { toast.error("Das Passwort darf nicht im Servernamen enthalten sein (Valheim-Regel)."); return; }
    }
    setPhase("installing");
    setInstallMsg("Server wird angelegt…");

    const steamAppId = steamGame?.isCustom ? customAppId.trim() : steamGame?.appId;
    const finalType =
      game === "bedrock" ? "bedrock"
      : game === "modpack" ? "modpack"
      : game === "steam" ? `steam:${steamAppId}`
      : type;
    const server = await serverStore.createServer({ name: name.trim(), port, ram_limit: ram, type: finalType });
    if (!server) { setPhase("steps"); return; }

    // Steam: kick off the (potentially multi-GB) SteamCMD install in the
    // background and jump to the game's "Spiel & Mods" tab where progress lives.
    if (game === "steam") {
      await serverStore.steamInstall(server.id, { slug: steamSlug, appId: customAppId, config: steamCfg, restart: false });
      await serverStore.fetchData();
      setInstallMsg(`${steamGame?.name || "Spiel"} wird im Hintergrund installiert — Fortschritt siehst du im Tab „Spiel & Mods".`);
      setPhase("done");
      setTimeout(() => { onClose(); navigate(`/server/${server.id}/software`); }, 1600);
      return;
    }

    // Modpack: nothing to install yet — the loader/version come from the pack the
    // user picks in the Modpacks section, so jump straight there.
    if (game === "modpack") {
      await serverStore.fetchData();
      setInstallMsg("Server angelegt! Wähle jetzt dein Modpack aus…");
      setPhase("done");
      setTimeout(() => { onClose(); navigate(`/server/${server.id}/modpacks`); }, 1000);
      return;
    }

    setInstallMsg(game === "bedrock" ? "Bedrock-Server wird heruntergeladen… (~200 MB)" : "Server-Software wird installiert… (kann etwas dauern)");
    try {
      const res = await doInstall(server.id);
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || "Installation fehlgeschlagen"); }
      await serverStore.fetchData();
      setPhase("done");
      setTimeout(() => { onClose(); navigate(`/server/${server.id}/overview`); }, 1000);
    } catch (e: any) {
      await serverStore.fetchData();
      setInstallMsg(e.message || "Installation fehlgeschlagen — der Server wurde angelegt, die Software aber nicht installiert.");
      setPhase("error");
      (window as any).__gm_created = server.id;
    }
  };

  const gameLabel =
    game === "bedrock" ? "Bedrock"
    : game === "modpack" ? "Modpack-Server"
    : game === "steam" ? (steamGame?.name || "Steam-Server")
    : TYPES.find((t) => t.id === type)?.title;

  return (
    <div className="fixed inset-0 bg-ink/75 flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-150" onMouseDown={(e) => { if (e.target === e.currentTarget && phase === "steps") onClose(); }}>
      <div className="bg-surface-2 border border-line rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="h-[2px] w-full gm-powerline" />

        {phase === "installing" || phase === "done" || phase === "error" ? (
          <div className="p-8 flex flex-col items-center text-center">
            {phase === "installing" && <Loader2 size={36} className="text-ember animate-spin" />}
            {phase === "done" && <CheckCircle2 size={40} className="text-online" />}
            {phase === "error" && <AlertTriangle size={38} className="text-warn" />}
            <h2 className="font-display text-lg font-semibold text-fg mt-4">
              {phase === "installing" ? "Wird eingerichtet…" : phase === "done" ? "Fertig!" : "Fast geschafft"}
            </h2>
            <p className="text-fg-muted text-sm mt-1.5 max-w-sm">{installMsg}</p>
            {phase === "error" && (
              <div className="flex gap-2 mt-5">
                <Button variant="secondary" onClick={onClose}>Schließen</Button>
                <Button onClick={() => { const id = (window as any).__gm_created; onClose(); if (id) navigate(`/server/${id}/software`); }}>Zum Server</Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-line flex items-center justify-between">
              <div>
                <h2 className="font-display text-[17px] font-semibold text-fg">Neuen Server einrichten</h2>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {flow.map((_, i) => (
                    <span key={i} className={cn("h-1 rounded-full transition-all", i <= stepIdx ? "bg-ember w-5" : "bg-white/10 w-2.5")} />
                  ))}
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 text-fg-muted hover:text-fg hover:bg-white/[0.06] rounded-lg transition-all" aria-label="Schließen"><X size={18} /></button>
            </div>

            <div className="p-5 min-h-[240px]">
              {step === "game" && (
                <div className="space-y-3">
                  <p className="text-sm text-fg-muted">Was möchtest du hosten?</p>
                  <OptionCard icon={<Box size={20} />} title="Minecraft: Java Edition" subtitle="Vanilla, Fabric, Paper, Purpur — Mods & Plugins" selected={game === "java"} onClick={() => setGame("java")} />
                  <div className="grid grid-cols-2 gap-3">
                    <OptionCard icon={<Boxes size={20} />} title="Bedrock" subtitle="Konsole / Mobile / Win10" selected={game === "bedrock"} onClick={() => setGame("bedrock")} />
                    <OptionCard icon={<Package size={20} />} title="Modpack" subtitle="Modrinth / CurseForge" selected={game === "modpack"} onClick={() => setGame("modpack")} />
                  </div>
                  <OptionCard icon={<Gamepad2 size={20} />} title="Steam-Server" subtitle="ARK (mit Mods!), Valheim, Palworld, Rust …" selected={game === "steam"} onClick={() => setGame("steam")} />
                </div>
              )}

              {step === "steamgame" && (
                <div className="space-y-3">
                  <p className="text-sm text-fg-muted">Welches Spiel möchtest du hosten?</p>
                  {steamGames.length === 0 ? (
                    <div className="flex items-center gap-2 text-fg-muted text-sm py-6"><Loader2 size={16} className="animate-spin" /> Steam-Katalog wird geladen…</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2.5 max-h-[260px] overflow-y-auto pr-1 -mr-1">
                      {steamGames.map((g) => (
                        <button
                          key={g.slug}
                          onClick={() => setSteamSlug(g.slug)}
                          className={cn(
                            "relative text-left rounded-xl border overflow-hidden transition-all bg-surface-2",
                            steamSlug === g.slug ? "border-ember/60 ring-1 ring-ember/40" : "border-line hover:border-line-strong"
                          )}
                        >
                          <div className="w-full h-14 bg-gradient-to-br from-surface-2 to-ink flex items-center justify-center relative">
                            <Gamepad2 size={18} className="text-fg-muted/40" />
                            {g.heroImage && (
                              <img src={g.heroImage} alt={g.name} className="absolute inset-0 w-full h-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                            )}
                          </div>
                          <div className="px-2.5 py-2">
                            <div className={cn("text-sm font-medium truncate", steamSlug === g.slug ? "text-ember" : "text-fg")}>{g.name}</div>
                            {g.supportsMods && <div className="text-[10px] text-fg-muted">Mods unterstützt</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {steamSlug === "custom" && (
                    <div className="space-y-2">
                      <Input label="Steam AppID des Dedicated Servers" value={customAppId} onChange={(e) => setCustomAppId(e.target.value)} placeholder="z.B. 2394010" />
                      {customPreview && (
                        <div className="flex items-center gap-3 rounded-lg bg-white/[0.03] border border-line p-2.5">
                          {customPreview.image && <img src={customPreview.image} alt="" className="w-16 h-8 rounded object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />}
                          <span className="text-sm text-fg truncate">{customPreview.name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {step === "type" && (
                <div className="space-y-3">
                  <p className="text-sm text-fg-muted">Server-Typ wählen.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {TYPES.map((t) => (
                      <OptionCard key={t.id} icon={t.icon} title={t.title} subtitle={t.subtitle} selected={type === t.id} onClick={() => setType(t.id)} />
                    ))}
                  </div>
                </div>
              )}

              {step === "version" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-fg-muted">Minecraft-Version</p>
                    <label className="flex items-center gap-2 text-xs text-fg-muted">
                      Snapshots <ToggleSwitch enabled={showSnapshots} onChange={setShowSnapshots} />
                    </label>
                  </div>
                  {loadingVersions ? (
                    <div className="flex items-center gap-2 text-fg-muted text-sm py-4"><Loader2 size={16} className="animate-spin" /> Versionen werden geladen…</div>
                  ) : (
                    <Select
                      value={selectedVersion}
                      onChange={setSelectedVersion}
                      placeholder="Version wählen"
                      options={filteredVersions.map((v) => ({ value: v.id, label: v.id, hint: v.release ? "Release" : "Snapshot" }))}
                    />
                  )}
                  <p className="text-xs text-fg-muted">Standard ist die neueste stabile Version.</p>
                </div>
              )}

              {step === "config" && (
                <div className="space-y-4">
                  <Input label="Servername" value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Survival mit Freunden" autoFocus />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Port" type="number" value={port} onChange={(e) => setPort(parseInt(e.target.value) || 0)} />
                    <div>
                      <label className="text-[11px] uppercase font-medium text-fg-muted mb-2 tracking-wider flex justify-between items-center">
                        <span>RAM</span>
                        <span className="text-ember font-mono bg-ember/10 border border-ember/20 px-2 py-0.5 rounded">{ram} GB</span>
                      </label>
                      <input type="range" min="1" max="32" step="1" value={ram} onChange={(e) => setRam(parseInt(e.target.value))} className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-ember mt-2.5" />
                    </div>
                  </div>

                  {game === "steam" && (
                    <div className="grid grid-cols-2 gap-4">
                      {["ark-se", "rust", "projectzomboid", "palworld"].includes(steamSlug) && (
                        <Input label="Admin-Passwort" value={steamCfg.adminPassword || ""} onChange={(e) => setSteamCfg({ ...steamCfg, adminPassword: e.target.value })} />
                      )}
                      {steamGame && "password" in (steamGame.defaults || {}) && (
                        <Input
                          label={steamSlug === "valheim" ? "Join-Passwort (min. 5 Zeichen)" : "Passwort (optional)"}
                          value={steamCfg.password ?? ""}
                          onChange={(e) => setSteamCfg({ ...steamCfg, password: e.target.value })}
                        />
                      )}
                      <Input label="Max. Spieler" type="number" value={steamCfg.maxPlayers ?? 20} onChange={(e) => setSteamCfg({ ...steamCfg, maxPlayers: parseInt(e.target.value) || 0 })} />
                      {steamGame?.maps?.length > 0 && (
                        <div className="col-span-2">
                          <Select label="Map" value={steamCfg.map || steamGame.maps[0]} onChange={(v) => setSteamCfg({ ...steamCfg, map: v })} options={steamGame.maps.map((m: string) => ({ value: m, label: m }))} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="rounded-lg bg-white/[0.03] border border-line px-3.5 py-2.5 text-xs text-fg-muted">
                    {game === "modpack"
                      ? "Erstellt den Server — im nächsten Schritt wählst du dein Modpack (Modrinth/CurseForge) aus."
                      : game === "steam"
                      ? `Legt ${gameLabel} an und startet den SteamCMD-Download (große Spiele dauern etwas). Mods verwaltest du danach im Tab „Spiel & Mods".`
                      : `Erstellt ${gameLabel}${game === "java" ? ` ${selectedVersion}` : " (neueste)"} und installiert direkt.`}
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-line flex items-center justify-between">
              <Button variant="ghost" onClick={stepIdx === 0 ? onClose : back}>
                {stepIdx === 0 ? "Abbrechen" : <><ArrowLeft size={16} /> Zurück</>}
              </Button>
              {step === "config" ? (
                <Button onClick={create} disabled={!name.trim()}>
                  <Check size={16} /> {game === "modpack" ? "Erstellen & Pack wählen" : "Erstellen & installieren"}
                </Button>
              ) : (
                <Button onClick={next} disabled={!canNext}>Weiter <ArrowRight size={16} /></Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});
